import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewDidLeave,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { chevronBackOutline } from 'ionicons/icons'
import {
  setAction,
  setFlow,
  setFiO2,
  setVia,
  setHumidity,
  setOralPharyngealAirway,
  setAirTemperature,
  setAirTemperatureUnit,
  setAirwayComplications,
  setPressure,
  setInspiratoryPressure,
  setExpiratoryPressure,
  setUpdateInputRows,
  saveAirway,
  setSize,
  setVerificationMethod,
  setAirwayDefaults,
  setReasonsForFailure,
  setAirwayComplicationsPNNV,
} from '../store/airway.actions'
import { getCurrentDate, getCurrentTime, randomNumber } from '../../../../utilities'
import { ProcedureConstants } from '../../../../components/procedure/store/procedure.constants'
import { AirwayConstants } from '../store/airway.constants'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { setInputRows, setIsFirstVisit } from '../../../../components/procedure/store/procedure.actions'
import { getAirwaySelector } from '../selectors'
import { getAirwayOptionsSelector, getAllPNNVOptionsSelector } from '../../../options/selectors'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import Procedure from '../../../../components/procedure/Procedure.component'
import { IOption } from '../../../../common/common.types'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { initialAirwayState } from '../store/airway.reducers'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import { getDefaultTempTypeSelector } from '../../../configurations/selectors/defaultTempType.selector'
import { getDefaultsSelector } from '../../../defaults/selectors/defaults.selector'
import { getAgeRangeGroupId, getDefaultsForAgeRange } from '../../../../utilities/procedures.util'
import { getPatientDemographicsSelector } from '../../../patientDemographics/selectors'
import { getProcedureStateSelector } from '../../../../components/procedure/selectors'
import { goBack } from '../../../../utilities/navigation.util'
import { getDatapointsConfigSelector } from '../../../configurations/selectors'
import { IDataPointsConfig, IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IAlertState } from '../../../../components/alert/store/alert.types'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'

const Airway: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const airway = useSelector(getAirwaySelector)
  const {
    date,
    time,
    assessedBy,
    successfulAttempt,
    pta,
    action,
    flow,
    fiO2,
    via,
    humidity,
    oralPharyngealAirway,
    airTemperature,
    airTemperatureBool,
    airTemperatureUnit,
    airwayComplications,
    airwayComplicationsPNNV,
    pressure,
    inspiratoryPressure,
    expiratoryPressure,
    procedureComplications,
    response,
    responsePNNV,
    authorization,
    comments,
    updateInputRows,
    attempts,
    size,
    rolePerforming,
    rolePerformingPNNV,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
    verificationMethod,
    isEkgImport,
    airwayDefaults,
    reasonsForFailure,
  } = airway
  const defaultTempType = useSelector(getDefaultTempTypeSelector)
  const airwayOptions = useSelector(getAirwayOptionsSelector)
  const { actionOptions, viaOptions, airwayComplicationsOptions, verificationMethodOptions, reasonsForFailureOptions } =
    airwayOptions
  const allPNNVOptions = useSelector(getAllPNNVOptionsSelector)
  const airwayComplicationsPNNVOptions = allPNNVOptions?.['eAirway.08']
  const { defaults } = useSelector(getDefaultsSelector)
  const { dOB, age, ageUnit } = useSelector(getPatientDemographicsSelector)
  const { isFirstVisit } = useSelector(getProcedureStateSelector)
  const { DP_AIRWAY_FAILURE }: IDataPointsConfig = useSelector(getDatapointsConfigSelector)
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  useEffect(() => {
    if (!airTemperatureUnit) {
      if (defaultTempType && defaultTempType === 'c') {
        dispatch(setAirTemperatureUnit(false))
      } else {
        dispatch(setAirTemperatureUnit(true))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (String(action?.name) === 'Oxygen' && size) {
      dispatch(setSize(null))
    }
    if (String(action?.name) !== 'CPAP' && pressure) {
      dispatch(setPressure(null))
    }
    if (String(action?.name) !== 'BiPAP' && (inspiratoryPressure || expiratoryPressure)) {
      dispatch(setInspiratoryPressure(null))
      dispatch(setExpiratoryPressure(null))
    }
    if (String(action?.id) !== 'Bag Valve Mask' && oralPharyngealAirway) {
      dispatch(setOralPharyngealAirway(false))
    }
    if (String(action?.name) !== 'Oxygen' && via) {
      dispatch(setVia(null))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action])

  const save = () => {
    dispatch(saveAirway())
  }

  const actionOnChange = (option: IOption) => {
    dispatch(setAction(option))
    save()
  }

  const flowOnChange = (values: string[]) => {
    dispatch(setFlow(values.join('')))
    save()
  }

  const fiO2OnChange = (values: string[]) => {
    dispatch(setFiO2(values.join('')))
    save()
  }

  const viaOnChange = (option: IOption) => {
    dispatch(setVia(option))
    save()
  }

  const humidityOnChange = (values: string[]) => {
    dispatch(setHumidity(values.join('')))
    save()
  }

  const airTemperatureOnChange = (values: string[]) => {
    dispatch(setAirTemperature(values.join('')))
    save()
  }

  const pressureOnChange = (values: string[]) => {
    dispatch(setPressure(values.join('')))
    save()
  }

  const inspiratoryPressureOnChange = (values: string[]) => {
    dispatch(setInspiratoryPressure(values.join('')))
    save()
  }

  const expiratoryPressureOnChange = (values: string[]) => {
    dispatch(setExpiratoryPressure(values.join('')))
    save()
  }

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = [
      'key',
      'addToTimeline',
      'updateInputRows',
      'airTemperatureUnit',
      'airTemperatureBool',
      'action',
      'flow',
      'airwayDefaults',
    ]
    const currentState = filterOutObjectKeys(airway, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialAirwayState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()

    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'airway/SET_TIME', time: time })
              dispatch({ type: 'airway/SET_DATE', date: date })
              save()
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: airway.key,
                rowActionType: TimelineConstants.SET_AIRWAY_PROCEDURE_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      const dateTimeValidationCheck = initialStateCheck()
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  const sizeOnChange = (values: string[]) => {
    dispatch(setSize(values.join('')))
    save()
  }

  const mergedAirwayComplicationsOptions = useMemo(() => {
    const baseOptions = [...(airwayComplicationsOptions ?? [])]
    const pnnvOptions = [...(airwayComplicationsPNNVOptions ?? [])]

    if (!pnnvOptions.length) {
      return baseOptions
    }

    const mergedMap = new Map<IOption['id'], IOption>()

    baseOptions.forEach((option) => {
      mergedMap.set(option.id, option)
    })

    pnnvOptions.forEach((option) => {
      const existingOption = mergedMap.get(option.id)
      if (existingOption) {
        mergedMap.set(option.id, { ...existingOption, ...option })
      } else {
        mergedMap.set(option.id, option)
      }
    })

    return Array.from(mergedMap.values())
  }, [airwayComplicationsOptions, airwayComplicationsPNNVOptions])

  const inputRows = [
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AirwayConstants.SET_ACTION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('airway.action'),
            dataTestId: 'Action',
            options: actionOptions,
            action: actionOnChange,
            listHeader: 'Action',
            selectedOption: action,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AirwayConstants.SET_FLOW,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: false,
          props: {
            label: t('airway.flow'),
            dataTestId: 'Flow',
            elementId: 'Flow',
            values: flow ? flow.split('') : [],
            onNumPadClick: flowOnChange,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AirwayConstants.SET_FIO2,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: false,
          props: {
            label: t('airway.fiO2'),
            dataTestId: 'FiO2',
            elementId: 'FiO2',
            values: fiO2 ? fiO2.split('') : [],
            onNumPadClick: fiO2OnChange,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AirwayConstants.SET_VIA,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('airway.via'),
            dataTestId: 'Via',
            options: viaOptions,
            action: viaOnChange,
            listHeader: 'Via',
            selectedOption: via,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AirwayConstants.SET_HUMIDITY,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: false,
          props: {
            label: t('airway.humidity'),
            dataTestId: 'Humidity',
            elementId: 'Humidity',
            values: humidity ? humidity.split('') : [],
            onNumPadClick: humidityOnChange,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AirwayConstants.SET_ORAL_PHARYNGEAL_AIRWAY,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('airway.oralPharyngealAirway'),
            dataTestId: 'OralPharyngealAirway',
            isChecked: oralPharyngealAirway,
            onChangeAction: setOralPharyngealAirway,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AirwayConstants.SET_AIR_TEMPERATURE,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: false,
          props: {
            label: t('airway.airTemperature'),
            dataTestId: 'AirTemperature',
            elementId: 'AirTemperature',
            values: airTemperature ? airTemperature.split('') : [],
            onNumPadClick: airTemperatureOnChange,
          },
        },
        {
          key: AirwayConstants.SET_AIR_TEMPERATURE_UNIT,
          type: ProcedureConstants.TOGGLE,
          className: 'temperature-ion-toggle',
          hidden: false,
          props: {
            toogleClassName: 'temperature-ion-toggle',
            rightLabel: t('airway.F'),
            leftLabel: t('airway.C'),
            dataTestId: 'AirTemperatureUnit',
            isChecked: airTemperatureBool,
            onChangeAction: setAirTemperatureUnit,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AirwayConstants.SET_AIRWAY_COMPLICATIONS,
          type: ProcedureConstants.MULTI_SELECT,
          hidden: false,
          props: {
            label: t('airway.airwayComplications'),
            dataTestID: 'AirwayComplications',
            options: mergedAirwayComplicationsOptions,
            action: setAirwayComplications,
            listHeader: 'Airway Complications',
            savedValues: airwayComplications,
            pnnvAction: setAirwayComplicationsPNNV,
            savedPNNVValue: airwayComplicationsPNNV,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AirwayConstants.SET_VERIFICATION_METHOD,
          type: ProcedureConstants.MULTI_SELECT,
          hidden: false,
          props: {
            label: t('airway.verificationMethod'),
            dataTestID: 'VerificationMethod',
            options: verificationMethodOptions,
            action: setVerificationMethod,
            listHeader: 'Verification Method',
            savedValues: verificationMethod,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AirwayConstants.SET_PRESSURE,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: false,
          props: {
            label: t('airway.pressure'),
            dataTestId: 'Pressure',
            elementId: 'Pressure',
            values: pressure ? pressure.split('') : [],
            onNumPadClick: pressureOnChange,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AirwayConstants.SET_INSPIRATORY_PRESSURE,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: false,
          props: {
            label: t('airway.inspiratoryPressure'),
            dataTestId: 'InspiratoryPressure',
            values: inspiratoryPressure ? inspiratoryPressure.split('') : [],
            onNumPadClick: inspiratoryPressureOnChange,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AirwayConstants.SET_EXPIRATORY_PRESSURE,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: false,
          props: {
            label: t('airway.expiratoryPressure'),
            dataTestId: 'ExpiratoryPressure',
            elementId: 'ExpiratoryPressure',
            values: expiratoryPressure ? expiratoryPressure.split('') : [],
            onNumPadClick: expiratoryPressureOnChange,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AirwayConstants.SET_SIZE,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: false,
          props: {
            label: t('airway.size'),
            dataTestId: 'Size',
            elementId: 'Size',
            values: size ? size.split('') : [],
            onNumPadClick: sizeOnChange,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: AirwayConstants.SET_REASONS_FOR_FAILURE,
          type: ProcedureConstants.MULTI_SELECT,
          hidden: !DP_AIRWAY_FAILURE || successfulAttempt,
          props: {
            label: t('airway.ReasonsForFailure'),
            dataTestID: 'ReasonsForFailure',
            options: reasonsForFailureOptions,
            action: setReasonsForFailure,
            listHeader: t('airway.ReasonsForFailure'),
            savedValues: reasonsForFailure || [],
          },
        },
      ],
    },
  ]

  const setActionDefault = () => {
    const actionDefault = actionOptions.find((option) => option.name === airwayDefaults?.defaultAction)

    if (actionDefault) {
      dispatch(setAction(actionDefault))
      return
    }

    dispatch(setUpdateInputRows(true))
  }

  const setFieldDefaults = () => {
    const fieldDefaults = airwayDefaults?.defaults.filter((field) => field.actionId === action?.name)

    fieldDefaults?.forEach((fieldDefault) => {
      const fieldName = fieldDefault.fieldName
      const defaultValue = fieldDefault.defaultValue

      if (fieldName === 'field1') {
        dispatch(setFlow(defaultValue))
      }
    })

    dispatch(setUpdateInputRows(true))
  }

  useIonViewWillEnter(() => {
    const ageRange = getAgeRangeGroupId(dOB, age, ageUnit)
    const defaultsForAgeRange = getDefaultsForAgeRange(defaults, 'Airway', ageRange)

    if (defaultsForAgeRange) dispatch(setAirwayDefaults(defaultsForAgeRange))
    else dispatch(setUpdateInputRows(true))
  }, [defaults, dOB, age, ageUnit])

  useEffect(() => {
    if (!isFirstVisit || !airwayDefaults || action) return

    setActionDefault()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airwayDefaults])

  useEffect(() => {
    if (!isFirstVisit || !airwayDefaults || !action) return

    setFieldDefaults()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, airwayDefaults])

  useIonViewDidLeave(() => {
    dispatch(setIsFirstVisit(false))
  })

  if (updateInputRows) {
    dispatch(setInputRows(inputRows))
    dispatch(setUpdateInputRows(false))
  }

  return (
    <IonPage>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>Airway</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light'>
        <Procedure
          type={'airway'}
          procedureDate={date}
          procedureTime={time}
          assessedBy={assessedBy}
          successfulAttempt={successfulAttempt}
          procedureComplications={procedureComplications}
          response={response}
          responsePNNV={responsePNNV}
          authorization={authorization}
          comments={comments}
          attempts={attempts}
          rolePerforming={rolePerforming}
          rolePerformingPNNV={rolePerformingPNNV}
          procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
          procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
          pta={pta}
          isEkgImport={isEkgImport}
        />
      </IonContent>
    </IonPage>
  )
}

export default Airway
